
import { signIn, signOut, getCurrentUser, fetchAuthSession, resetPassword, confirmResetPassword, confirmSignIn, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import { authLogger } from '../../utils/logger';

// Extend Window interface for test overrides
declare global {
  interface Window {
    __TEST_MOCK_MODE__?: boolean;
  }
}

// Dynamic mock mode checker (evaluated at runtime)
const getMockMode = (): boolean => {
  // Check for test override first (set by tests)
  if (typeof window !== 'undefined') {
    // Check window flag
    if (typeof window.__TEST_MOCK_MODE__ !== 'undefined') {
      return window.__TEST_MOCK_MODE__;
    }
    
    // Check localStorage
    try {
      const testMode = localStorage.getItem('TEST_MOCK_MODE');
      if (testMode !== null) {
        return testMode === 'true';
      }
    } catch (e) {
      // localStorage might not be available
    }
    
    // Check URL parameter
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const mockParam = urlParams.get('mock');
      if (mockParam !== null) {
        return mockParam === 'true';
      }
    } catch (e) {
      // URL parsing might fail
    }
  }
  
  // Fallback to import.meta.env
  return import.meta.env.VITE_MOCK_MODE === 'true';
};

export const authService = {
  /**
   * Sign in with username and password
   */
  signIn: async (username: string, password: string) => {
    if (getMockMode()) {
      // Mock authentication for development
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (username === 'demo@fabrik28.ai' && password === 'demo123') {
        return {
          isSignedIn: true,
          username: username
        };
      } else {
        throw new Error('Invalid credentials');
      }
    }

    try {
      authLogger.debug('🔍 AuthService: Attempting signIn with:', { username, password: '***' });
      
      const { isSignedIn, nextStep } = await signIn({ username, password });
      
      authLogger.debug('🔍 AuthService: SignIn successful:', { isSignedIn, nextStep });
      
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return {
          challengeName: 'NEW_PASSWORD_REQUIRED',
          username
        };
      }
      
      // Handle case where user needs to confirm their signup (email verification)
      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        authLogger.debug('🔍 AuthService: User needs to confirm signup, throwing UserNotConfirmedException');
        throw new Error('User is not confirmed.');
      }
      
      // Check if user has completed registration by checking custom attribute
      if (isSignedIn) {
        try {
          const userAttributes = await fetchUserAttributes();
          authLogger.debug('🔍 AuthService: User attributes:', userAttributes);

          return { 
            isSignedIn, 
            username,
          };
        } catch (attributeError) {
          authLogger.error('🔍 AuthService: Error fetching user attributes:', attributeError);
          // If we can't fetch attributes, return basic info
          return { 
            isSignedIn, 
            username
          };
        }
      }

      return { isSignedIn, username };
    } catch (error) {
      authLogger.error('🔍 AuthService: Raw Cognito error:', error);
      authLogger.error('🔍 AuthService: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
        stack: error instanceof Error ? error.stack : 'No stack',
        fullError: error
      });
      
      // Check if this is an AWS Amplify error with additional properties
      if (error && typeof error === 'object') {
        
        // Check for common AWS error properties
        const errorObj = error as any;
        if (errorObj.response) {
          authLogger.debug('🔍 AuthService: Error.response:', errorObj.response);
        }
        if (errorObj.data) {
          authLogger.debug('🔍 AuthService: Error.data:', errorObj.data);
        }
        if (errorObj.code) {
          authLogger.debug('🔍 AuthService: Error.code:', errorObj.code);
        }
        if (errorObj.__type) {
          authLogger.debug('🔍 AuthService: Error.__type:', errorObj.__type);
        }
        if (errorObj.statusCode) {
          authLogger.debug('🔍 AuthService: Error.statusCode:', errorObj.statusCode);
        }
        if (errorObj.cause) {
          authLogger.debug('🔍 AuthService: Error.cause:', errorObj.cause);
        }
        if (errorObj.underlyingError) {
          authLogger.debug('🔍 AuthService: Error.underlyingError:', errorObj.underlyingError);
        }
        
        // Try to stringify the entire error object
        try {
          authLogger.debug('🔍 AuthService: Error JSON:', JSON.stringify(error, null, 2));
        } catch (jsonError) {
          authLogger.debug('🔍 AuthService: Error cannot be JSON stringified:', jsonError);
        }
      }
      
      // Log the exact error message for debugging
      const errorMessage = error instanceof Error ? error.message : String(error);
      authLogger.debug('🔍 AuthService: Exact error message from Cognito:', `"${errorMessage}"`);
      
      throw error;
    }
  },

  /**
   * Sign out the current user
   */
  signOut: async () => {
    if (getMockMode()) {
      // Mock sign out
      return;
    }

    try {
      await signOut();
    } catch (error) {
      authLogger.error('Error signing out:', error);
      throw error;
    }
  },

  /**
   * Get the current authenticated user with attributes
   */
  getCurrentUser: async () => {
    if (getMockMode()) {
      // Mock user data
      authLogger.debug('🔍 AuthService: Using mock mode for getCurrentUser');
      return {
        userId: 'demo-user-123',
        username: 'demo@fabrik28.ai',
        attributes: {
          email: 'demo@fabrik28.ai',
          given_name: 'Demo',
          family_name: 'User',
          name: 'Demo User'
        }
      };
    }

    try {
      authLogger.debug('🔍 AuthService: Calling Cognito getCurrentUser...');
      const user = await getCurrentUser();
      
      authLogger.debug('🔍 AuthService: Raw Cognito user response:', {
        userId: user.userId,
        username: user.username,
        signInDetails: user.signInDetails
      });
      
      // Fetch user attributes separately (required in Amplify v6)
      authLogger.debug('🔍 AuthService: Fetching user attributes...');
      const attributes = await fetchUserAttributes();
      
      authLogger.debug('🔍 AuthService: Fetched user attributes:', attributes);
      
      // Log each attribute individually
      if (attributes) {
        authLogger.debug('🔍 AuthService: User attributes breakdown:');
        Object.entries(attributes).forEach(([key, value]) => {
          authLogger.debug(`  - ${key}: "${value}"`);
        });
      }
      
      const result = {
        userId: user.userId,
        username: user.username,
        attributes: attributes || {}
      };
      
      authLogger.debug('🔍 AuthService: Processed user result:', result);
      return result;
    } catch (error) {
      authLogger.error('❌ AuthService: Error getting current user:', error);
      return null;
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: async () => {
    if (getMockMode()) {
      // In mock mode, consider user authenticated if they've "logged in"
      // This would typically be managed by your app state
      return false;
    }

    try {
      await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Get the current session
   */
  getCurrentSession: async () => {
    if (getMockMode()) {
      // Mock session data
      return {
        tokens: {
          idToken: {
            toString: () => 'mock-jwt-token'
          }
        }
      };
    }

    try {
      return await fetchAuthSession();
    } catch (error) {
      authLogger.error('Error getting current session:', error);
      return null;
    }
  },

  /**
   * Get JWT token for API calls
   */
  getJwtToken: async () => {
    if (getMockMode()) {
      // Return mock token
      return 'mock-jwt-token';
    }

    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (error) {
      authLogger.error('Error getting JWT token:', error);
      return null;
    }
  },

  /**
   * Initiate password reset - sends verification code to user's email
   */
  resetPassword: async (username: string) => {
    if (getMockMode()) {
      // Mock password reset
      await new Promise(resolve => setTimeout(resolve, 1000));
      return {
        destination: username.replace(/(.{2})(.*)(@.*)/, '$1***$3')
      };
    }

    try {
      const output = await resetPassword({ username });
      return {
        destination: output.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE' 
          ? output.nextStep.codeDeliveryDetails?.destination 
          : undefined
      };
    } catch (error) {
      authLogger.error('Error initiating password reset:', error);
      throw error;
    }
  },

  /**
   * Confirm password reset with verification code and new password
   */
  confirmResetPassword: async (username: string, confirmationCode: string, newPassword: string) => {
    if (getMockMode()) {
      // Mock password reset confirmation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (confirmationCode === '123456') {
        return { success: true };
      } else {
        throw new Error('Invalid verification code');
      }
    }

    try {
      await confirmResetPassword({ username, confirmationCode, newPassword });
      return { success: true };
    } catch (error) {
      authLogger.error('Error confirming password reset:', error);
      throw error;
    }
  },

  /**
   * Complete new password challenge for admin-created users
   */
  completeNewPasswordChallenge: async (username: string, temporaryPassword: string, newPassword: string) => {
    if (getMockMode()) {
      authLogger.debug('🔍 AuthService: Using mock mode for completeNewPasswordChallenge');
      // Mock new password challenge completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // In mock mode, accept any temporary password
      return { success: true };
    }

    try {
      authLogger.debug('🔍 AuthService: Starting new password challenge for:', username);
      
      // First, sign in with temporary password to get the challenge
      const signInResult = await signIn({ username, password: temporaryPassword });
      authLogger.debug('🔍 AuthService: SignIn result:', signInResult);
      
      if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        authLogger.debug('🔍 AuthService: Confirming new password...');
        
        // Complete the new password challenge
        const result = await confirmSignIn({
          challengeResponse: newPassword
        });
        
        authLogger.debug('🔍 AuthService: ConfirmSignIn result:', result);
        
        return { success: result.isSignedIn };
      } else {
        authLogger.error('❌ AuthService: Unexpected sign-in step:', signInResult.nextStep.signInStep);
        throw new Error('New password challenge not required or invalid temporary password');
      }
    } catch (error) {
      authLogger.error('❌ AuthService: Error completing new password challenge:', error);
      throw error;
    }
  },

  /**
   * Update user attributes
   */
  updateUserAttributes: async (attributes: Record<string, string>) => {
    if (getMockMode()) {
      authLogger.debug('🔍 AuthService: Mock mode - updateUserAttributes called with:', attributes);
      return { success: true };
    }

    try {
      authLogger.debug('🔍 AuthService: Updating user attributes:', attributes);
      await updateUserAttributes({
        userAttributes: attributes
      });
      authLogger.debug('🔍 AuthService: User attributes updated successfully');
      return { success: true };
    } catch (error) {
      authLogger.error('🔍 AuthService: Error updating user attributes:', error);
      throw error;
    }
  },

  /**
   * Fetch user attributes
   */
  fetchUserAttributes: async () => {
    if (getMockMode()) {
      authLogger.debug('🔍 AuthService: Mock mode - fetchUserAttributes');
      return {
        'email': 'demo@fabrik28.ai',
        'given_name': 'Demo',
        'family_name': 'User'
      };
    }

    try {
      authLogger.debug('🔍 AuthService: Fetching user attributes...');
      const attributes = await fetchUserAttributes();
      authLogger.debug('🔍 AuthService: User attributes fetched:', attributes);
      return attributes;
    } catch (error) {
      authLogger.error('🔍 AuthService: Error fetching user attributes:', error);
      throw error;
    }
  },

  /**
   * Initiate forgot password flow
   */
  forgotPassword: async (email: string) => {
    if (getMockMode()) {
      authLogger.debug('🔍 AuthService: Mock mode - forgotPassword');
      await new Promise(resolve => setTimeout(resolve, 1000));
      return {
        destination: email.replace(/(.{2})(.*)(@.*)/, '$1***$3')
      };
    }

    try {
      authLogger.debug('🔍 AuthService: Initiating forgot password for:', email);
      const result = await resetPassword({ username: email });
      authLogger.debug('🔍 AuthService: Forgot password initiated:', result);
      return {
        destination: result.nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE' 
          ? result.nextStep.codeDeliveryDetails?.destination 
          : email
      };
    } catch (error) {
      authLogger.error('❌ AuthService: Error initiating forgot password:', error);
      throw error;
    }
  },

  /**
   * Confirm forgot password with verification code
   */
  confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
    if (getMockMode()) {
      authLogger.debug('🔍 AuthService: Mock mode - confirmForgotPassword');
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true };
    }

    try {
      authLogger.debug('🔍 AuthService: Confirming forgot password for:', email);
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword
      });
      authLogger.debug('🔍 AuthService: Password reset confirmed successfully');
      return { success: true };
    } catch (error) {
      authLogger.error('❌ AuthService: Error confirming forgot password:', error);
      throw error;
    }
  }
};
